import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Calculator, Play, Target, TrendingUp,
  Clock, ChevronRight, Star, Zap, BookOpen, BarChart3
} from "lucide-react";

/**
 * Banking Quantitative Aptitude Practice Hub
 */
const BankingQuantPage = () => {
  const navigate = useNavigate();

  const topics = [
    {
      id: 'number-series',
      name: 'Number Series',
      description: 'Missing numbers, wrong terms',
      questions: 100,
      practiced: 0,
      difficulty: 'Medium',
      icon: '🔢',
      color: 'bg-blue-500'
    },
    {
      id: 'simplification',
      name: 'Simplification & Approximation',
      description: 'BODMAS, quick calculations',
      questions: 150,
      practiced: 0,
      difficulty: 'Easy',
      icon: '➕',
      color: 'bg-emerald-500'
    },
    {
      id: 'data-interpretation',
      name: 'Data Interpretation',
      description: 'Tables, Bar, Line, Pie charts',
      questions: 200,
      practiced: 0,
      difficulty: 'High',
      icon: '📊',
      color: 'bg-purple-500'
    },
    {
      id: 'quadratic',
      name: 'Quadratic Equations',
      description: 'Find x and y values',
      questions: 80,
      practiced: 0,
      difficulty: 'Easy',
      icon: '📐',
      color: 'bg-amber-500'
    },
    {
      id: 'arithmetic',
      name: 'Arithmetic',
      description: 'Percentage, Profit/Loss, SI/CI',
      questions: 300,
      practiced: 0,
      difficulty: 'Medium',
      icon: '🧮',
      color: 'bg-pink-500'
    },
    {
      id: 'time-work',
      name: 'Time & Work',
      description: 'Pipes, cisterns, efficiency',
      questions: 120,
      practiced: 0,
      difficulty: 'Medium',
      icon: '⏱️',
      color: 'bg-indigo-500'
    },
    {
      id: 'speed-distance',
      name: 'Speed & Distance',
      description: 'Trains, boats, relative speed',
      questions: 100,
      practiced: 0,
      difficulty: 'Medium',
      icon: '🚂',
      color: 'bg-teal-500'
    },
    {
      id: 'probability',
      name: 'Probability & P&C',
      description: 'Permutation, combination, odds',
      questions: 80,
      practiced: 0,
      difficulty: 'High',
      icon: '🎲',
      color: 'bg-rose-500'
    }
  ];

  const getDifficultyColor = (diff) => {
    switch(diff) {
      case 'Easy': return 'bg-emerald-100 text-emerald-700';
      case 'Medium': return 'bg-amber-100 text-amber-700';
      case 'High': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/banking')}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Quantitative Aptitude</h1>
                <p className="text-emerald-200 text-sm">35 Marks in Prelims | 60 Marks in Mains</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-emerald-200">Questions Done</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0%</p>
              <p className="text-xs text-emerald-200">Accuracy</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-emerald-200">Avg. Time</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">8</p>
              <p className="text-xs text-emerald-200">Topics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Practice */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Quick Practice Session
                </h3>
                <p className="text-slate-400 text-sm">25 mixed questions from all topics</p>
              </div>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => navigate('/banking/quant/practice?section=quant&mode=mixed')}
              >
                <Play className="w-4 h-4 mr-2" /> Start Practice
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Topics Grid */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Practice by Topic</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {topics.map((topic) => (
              <Card
                key={topic.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 border-transparent hover:border-emerald-200"
                onClick={() => navigate(`/banking/quant/practice?section=quant&topic=${topic.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 ${topic.color} rounded-lg flex items-center justify-center`}>
                      <span className="text-xl">{topic.icon}</span>
                    </div>
                    <Badge className={getDifficultyColor(topic.difficulty)}>
                      {topic.difficulty}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{topic.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">{topic.description}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{topic.practiced}/{topic.questions} done</span>
                    <span>{Math.round((topic.practiced/topic.questions)*100)}%</span>
                  </div>
                  <Progress value={(topic.practiced/topic.questions)*100} className="h-1.5 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Speed Tests */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Speed Tests</h3>
                <p className="text-sm text-slate-500">Timed practice to improve speed</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/banking/quant/speed-tests')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <button className="p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors text-left">
                <Clock className="w-5 h-5 text-emerald-600 mb-2" />
                <p className="font-medium text-slate-900">5-Min Sprint</p>
                <p className="text-xs text-slate-500">10 questions</p>
              </button>
              <button className="p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-left">
                <Clock className="w-5 h-5 text-blue-600 mb-2" />
                <p className="font-medium text-slate-900">10-Min Test</p>
                <p className="text-xs text-slate-500">20 questions</p>
              </button>
              <button className="p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors text-left">
                <Clock className="w-5 h-5 text-purple-600 mb-2" />
                <p className="font-medium text-slate-900">20-Min Mock</p>
                <p className="text-xs text-slate-500">35 questions</p>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" /> Quant Tips for Banking Exams
            </h3>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>- Master Number Series - 5 questions guaranteed in every exam</li>
              <li>- Learn squares (1-30), cubes (1-15), tables (1-20) by heart</li>
              <li>- Practice DI daily - it's the highest weightage section</li>
              <li>- Use approximation for simplification - don't calculate exact values</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankingQuantPage;
